import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useCurrentUser } from '@/hooks/useAuth';

interface Workspace {
  id: string;
  companyId: string;
  name: string;
}

interface Company {
  id: string;
  name: string;
}

export function AppHome() {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const companies = useQuery<Company[]>({
    queryKey: ['companies', 'mine'],
    queryFn: async () => (await api.get<Company[]>('/api/companies')).data,
    enabled: !!me.data,
  });

  const firstCompanyId = companies.data?.[0]?.id;
  const workspaces = useQuery<Workspace[]>({
    queryKey: ['workspaces', firstCompanyId],
    queryFn: async () =>
      (
        await api.get<Workspace[]>('/api/workspaces', {
          params: { companyId: firstCompanyId },
        })
      ).data,
    enabled: !!firstCompanyId,
  });

  useEffect(() => {
    if (me.isLoading) return;
    if (!me.data) {
      const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || '';
      window.location.href = `${apiBase}/auth/google`;
      return;
    }
    if (companies.data && companies.data.length === 0) {
      navigate('/onboarding');
      return;
    }
    if (workspaces.data && workspaces.data.length > 0) {
      navigate(`/app/workspace/${workspaces.data[0].id}/feed`);
    }
  }, [me.isLoading, me.data, companies.data, workspaces.data, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center text-ink-500 text-sm">
      {t('app.loading_workspace')}
    </div>
  );
}
